import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import {
  devApi,
  StatsData,
  ModuleListItem,
  ModuleDetail,
  TestRun,
  TrendPoint,
  FilterState,
  TestRunFilters,
  TrendFilters,
} from '../services/devApi';

interface LoadingState {
  stats: boolean;
  modules: boolean;
  moduleDetail: boolean;
  testRuns: boolean;
  testTrend: boolean;
}

export function useDevDashboard() {
  // Data state
  const [stats, setStats] = useState<StatsData | null>(null);
  const [modules, setModules] = useState<ModuleListItem[]>([]);
  const [modulesTotal, setModulesTotal] = useState(0);
  const [moduleDetail, setModuleDetail] = useState<ModuleDetail | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [testRunsTotal, setTestRunsTotal] = useState(0);
  const [testTrend, setTestTrend] = useState<TrendPoint[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Loading state
  const [loading, setLoading] = useState<LoadingState>({
    stats: false,
    modules: false,
    moduleDetail: false,
    testRuns: false,
    testTrend: false,
  });

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: '',
    page: 1,
    size: 50,
  });

  const [testRunFilters, setTestRunFilters] = useState<TestRunFilters>({
    page: 1,
    size: 10,
  });

  const [trendFilters, setTrendFilters] = useState<TrendFilters>({
    limit: 20,
  });

  // Ref to track if initial load has been done
  const initialLoadDone = useRef(false);

  // ============================================================
  // Fetch functions
  // ============================================================

  const fetchStats = useCallback(async () => {
    setLoading((prev) => ({ ...prev, stats: true }));
    try {
      const data = await devApi.getStats();
      setStats(data);
    } catch {
      // API not ready yet - degrade gracefully
      setStats(null);
    } finally {
      setLoading((prev) => ({ ...prev, stats: false }));
    }
  }, []);

  const fetchModules = useCallback(async (f?: Partial<FilterState>) => {
    const merged = { ...filters, ...f };
    setLoading((prev) => ({ ...prev, modules: true }));
    try {
      const params: Record<string, unknown> = {
        page: merged.page,
        size: merged.size,
      };
      if (merged.status) params.status = merged.status;
      if (merged.search) params.search = merged.search;
      if (merged.sortBy) params.sortBy = merged.sortBy;
      if (merged.order) params.order = merged.order;

      const res = await devApi.getModules(params as { page?: number; size?: number; status?: string; search?: string; sortBy?: string; order?: string });
      setModules(res.data || []);
      setModulesTotal(res.total || 0);
    } catch {
      setModules([]);
      setModulesTotal(0);
    } finally {
      setLoading((prev) => ({ ...prev, modules: false }));
    }
  }, [filters]);

  const fetchModuleDetail = useCallback(async (formId: string) => {
    setLoading((prev) => ({ ...prev, moduleDetail: true }));
    try {
      const data = await devApi.getModule(formId);
      setModuleDetail(data);
      setDrawerVisible(true);
    } catch {
      message.error('無法載入模組詳情');
      setModuleDetail(null);
    } finally {
      setLoading((prev) => ({ ...prev, moduleDetail: false }));
    }
  }, []);

  const fetchTestRuns = useCallback(async (f?: Partial<TestRunFilters>) => {
    const merged = { ...testRunFilters, ...f };
    setLoading((prev) => ({ ...prev, testRuns: true }));
    try {
      const params: Record<string, unknown> = {
        page: merged.page,
        size: merged.size,
      };
      if (merged.formId) params.formId = merged.formId;
      if (merged.category) params.category = merged.category;
      if (merged.failOnly) params.failOnly = merged.failOnly;

      const res = await devApi.getTestRuns(params as { page?: number; size?: number; formId?: string; category?: string; failOnly?: boolean });
      setTestRuns(res.data || []);
      setTestRunsTotal(res.total || 0);
    } catch {
      setTestRuns([]);
      setTestRunsTotal(0);
    } finally {
      setLoading((prev) => ({ ...prev, testRuns: false }));
    }
  }, [testRunFilters]);

  const fetchTestTrend = useCallback(async (f?: Partial<TrendFilters>) => {
    const merged = { ...trendFilters, ...f };
    setLoading((prev) => ({ ...prev, testTrend: true }));
    try {
      const params: Record<string, unknown> = {};
      if (merged.formId) params.formId = merged.formId;
      if (merged.category) params.category = merged.category;
      if (merged.limit) params.limit = merged.limit;

      const data = await devApi.getTestTrend(params as { formId?: string; category?: string; limit?: number });
      setTestTrend(data || []);
    } catch {
      setTestTrend([]);
    } finally {
      setLoading((prev) => ({ ...prev, testTrend: false }));
    }
  }, [trendFilters]);

  // ============================================================
  // Actions
  // ============================================================

  const refreshAll = useCallback(async () => {
    setLastUpdated(new Date());
    await Promise.all([
      fetchStats(),
      fetchModules(),
      fetchTestRuns(),
      fetchTestTrend(),
    ]);
    message.success('已刷新');
  }, [fetchStats, fetchModules, fetchTestRuns, fetchTestTrend]);

  const handleStatusFilter = useCallback((status: string) => {
    const newFilters = { ...filters, status, page: 1 };
    setFilters(newFilters);
    fetchModules(newFilters);
  }, [filters, fetchModules]);

  const handleSearch = useCallback((search: string) => {
    const newFilters = { ...filters, search, page: 1 };
    setFilters(newFilters);
    fetchModules(newFilters);
  }, [filters, fetchModules]);

  const handlePageChange = useCallback((page: number, size: number) => {
    const newFilters = { ...filters, page, size };
    setFilters(newFilters);
    fetchModules(newFilters);
  }, [filters, fetchModules]);

  const handleSort = useCallback((sortBy?: string, order?: 'asc' | 'desc') => {
    const newFilters = { ...filters, sortBy, order, page: 1 };
    setFilters(newFilters);
    fetchModules(newFilters);
  }, [filters, fetchModules]);

  const handleModuleClick = useCallback((formId: string) => {
    fetchModuleDetail(formId);
  }, [fetchModuleDetail]);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    setModuleDetail(null);
  }, []);

  const handleTestRunFilterChange = useCallback((newFilters: Partial<TestRunFilters>) => {
    const merged = { ...testRunFilters, ...newFilters, page: 1 };
    setTestRunFilters(merged);
    fetchTestRuns(merged);
  }, [testRunFilters, fetchTestRuns]);

  const handleTrendFilterChange = useCallback((newFilters: Partial<TrendFilters>) => {
    const merged = { ...trendFilters, ...newFilters };
    setTrendFilters(merged);
    fetchTestTrend(merged);
  }, [trendFilters, fetchTestTrend]);

  // ============================================================
  // Initial load
  // ============================================================

  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      setLastUpdated(new Date());
      Promise.all([
        fetchStats(),
        fetchModules(),
        fetchTestRuns(),
        fetchTestTrend(),
      ]);
    }
  }, [fetchStats, fetchModules, fetchTestRuns, fetchTestTrend]);

  return {
    // Data
    stats,
    modules,
    modulesTotal,
    moduleDetail,
    drawerVisible,
    testRuns,
    testRunsTotal,
    testTrend,
    lastUpdated,
    loading,
    filters,
    testRunFilters,
    trendFilters,

    // Actions
    refreshAll,
    handleStatusFilter,
    handleSearch,
    handlePageChange,
    handleSort,
    handleModuleClick,
    handleDrawerClose,
    handleTestRunFilterChange,
    handleTrendFilterChange,
  };
}
